/**
 * AdminInvoiceTools — Wartungs-Cockpit für Rechnungs-PDFs.
 *
 * Iter88zr (2026-02-19) — Nach Layout-Änderungen im PDF-Template
 * (z. B. Iter88zn: 2-Seiten → 1-Seite) bleiben bestehende, im
 * Object-Storage abgelegte PDFs unverändert. Diese Seite bietet
 * Admins/Managern einen 1-Klick-Bulk-Renderer, der alle Rechnungen
 * des Mandanten mit dem AKTUELLEN Template neu rendert (Nummer +
 * Datum bleiben).
 *
 * Ablauf: Scan (dry_run=true) → Liste anzeigen → „Alle jetzt neu
 * rendern" bestätigen → Report anzeigen.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, FileText, Search, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";

export default function AdminInvoiceTools() {
  const [scanning, setScanning] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [scanResult, setScanResult] = useState(null);   // { total, processed[] }
  const [runResult, setRunResult] = useState(null);     // { total, processed[], errors[] }

  const runScan = async () => {
    setScanning(true);
    setScanResult(null);
    setRunResult(null);
    try {
      const r = await api.post("/admin/invoices/regenerate-all-pdfs?dry_run=true");
      setScanResult(r.data);
      toast.success(`Scan fertig: ${r.data?.total || 0} Rechnung(en) gefunden`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan fehlgeschlagen");
    } finally {
      setScanning(false);
    }
  };

  const runBulk = async () => {
    const total = scanResult?.total || 0;
    if (!total) {
      toast.error("Bitte zuerst scannen");
      return;
    }
    if (!window.confirm(
      `${total} Rechnungs-PDF${total === 1 ? "" : "s"} werden jetzt mit dem AKTUELLEN ` +
      `Layout neu gerendert. Rechnungsnummern und Rechnungsdaten bleiben unverändert. ` +
      `Die alten PDFs im Object Storage werden überschrieben.\n\nFortfahren?`
    )) return;
    setRegenerating(true);
    setRunResult(null);
    try {
      const r = await api.post("/admin/invoices/regenerate-all-pdfs?dry_run=false");
      setRunResult(r.data);
      const ok_count = (r.data?.processed || []).length;
      const err_count = (r.data?.errors || []).length;
      if (err_count === 0) {
        toast.success(`${ok_count} Rechnung${ok_count === 1 ? "" : "en"} erfolgreich neu gerendert`);
      } else {
        toast.warning(`${ok_count} erfolgreich, ${err_count} fehlgeschlagen`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Massen-Regenerierung fehlgeschlagen");
    } finally {
      setRegenerating(false);
    }
  };

  const list = runResult || scanResult;

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 p-6" data-testid="admin-invoice-tools-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/dashboard" className="text-zinc-500 hover:text-zinc-300 text-[12px] inline-flex items-center gap-1 mb-2">
              <ArrowLeft size={12} /> Zurück zum Dashboard
            </Link>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <RefreshCw size={22} className="text-emerald-400" />
              Rechnungs-PDF-Werkzeuge
            </h1>
            <p className="text-zinc-500 text-[13px] mt-1">
              Nach Layout-Änderungen bestehende Rechnungs-PDFs im Object Storage einmalig neu rendern.
              Rechnungsnummern und Rechnungsdaten bleiben unverändert.
            </p>
          </div>
        </div>

        {/* Card: Scan + Bulk */}
        <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
              <FileText size={18} className="text-emerald-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-[15px] font-bold">Alle Rechnungs-PDFs neu rendern</h2>
              <p className="text-zinc-500 text-[12.5px] mt-0.5">
                Sinnvoll nach Deploys mit PDF-Layout-Änderungen (z. B. 2-Seiten → 1-Seite).
                Läuft mandantenweit über alle AZ1/AZ2/AZ3/AZ2_combined-Rechnungen.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={runScan}
              disabled={scanning || regenerating}
              data-testid="scan-invoices-btn"
              className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-[13px] inline-flex items-center gap-2 disabled:opacity-50"
            >
              {scanning ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              1. Scannen (dry run)
            </button>
            <button
              onClick={runBulk}
              disabled={!scanResult || scanning || regenerating || (scanResult?.total || 0) === 0}
              data-testid="regenerate-all-invoices-btn"
              className="h-10 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-[13px] inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {regenerating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              2. Alle jetzt neu rendern
              {scanResult?.total > 0 && !runResult ? ` (${scanResult.total})` : ""}
            </button>
          </div>

          {runResult && (
            <div className={`rounded-lg border p-3 flex items-start gap-2 text-[12.5px] ${
              (runResult.errors || []).length === 0
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                : "bg-amber-500/10 border-amber-500/30 text-amber-300"
            }`}>
              {(runResult.errors || []).length === 0
                ? <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
                : <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />}
              <div>
                <div className="font-semibold">
                  {(runResult.processed || []).length} von {runResult.total} Rechnung{runResult.total === 1 ? "" : "en"} erfolgreich neu gerendert
                </div>
                {(runResult.errors || []).length > 0 && (
                  <div className="mt-1">
                    {runResult.errors.length} Fehler — siehe Liste unten.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Table: Result */}
        {list && (
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="invoice-tools-result-table">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div className="text-[13px] font-semibold">
                {runResult ? "Ergebnis" : "Betroffene Rechnungen (Scan-Vorschau)"}
              </div>
              <div className="text-[11.5px] text-zinc-500 font-mono">
                Gesamt: {list.total || 0}
              </div>
            </div>
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-zinc-800">
                  <th className="px-4 py-2 font-normal">#</th>
                  <th className="px-4 py-2 font-normal">Rechnungsnr.</th>
                  <th className="px-4 py-2 font-normal">Angebot</th>
                  <th className="px-4 py-2 font-normal">Kind</th>
                  {runResult && <th className="px-4 py-2 font-normal">Größe (kB)</th>}
                  <th className="px-4 py-2 font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {[...(list.processed || []), ...(list.errors || [])].map((row, i) => (
                  <tr key={i} className="border-b border-zinc-900 hover:bg-zinc-900/50">
                    <td className="px-4 py-2 text-zinc-500 font-mono">{i + 1}</td>
                    <td className="px-4 py-2 font-mono">{row.invoice_number || "—"}</td>
                    <td className="px-4 py-2 text-zinc-400 font-mono">{row.offer_number || "—"}</td>
                    <td className="px-4 py-2 text-zinc-400">{row.kind || "—"}</td>
                    {runResult && (
                      <td className="px-4 py-2 text-zinc-400 font-mono">
                        {row.size_bytes ? Math.round(row.size_bytes / 1024) : "—"}
                      </td>
                    )}
                    <td className="px-4 py-2">
                      {row.error
                        ? <span className="text-amber-400">✗ {row.error}</span>
                        : runResult
                          ? <span className="text-emerald-400">✓ OK</span>
                          : <span className="text-zinc-500">bereit</span>
                      }
                    </td>
                  </tr>
                ))}
                {(list.processed || []).length === 0 && (list.errors || []).length === 0 && (
                  <tr>
                    <td colSpan={runResult ? 6 : 5} className="px-4 py-6 text-center text-zinc-500">
                      Keine Rechnungen gefunden.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
